import React from 'react';
import { Question } from '../../types/crime-game';
import { CheckCircle2, HelpCircle, XCircle, Eye, SkipForward } from 'lucide-react';
import { ClickableText } from './ClickableText';

interface AnsweredQuestionsLogProps {
  questions: Question[];
  answeredQuestions: Set<string>;
  correctlyAnsweredQuestions: Set<string>;
  wronglyAnsweredQuestions: Set<string>;
  revealedAnswers: Set<string>;
  skippedQuestions: Set<string>;
  onWordClick?: (word: string) => boolean;
  pendingWord?: string | null;
}

export const AnsweredQuestionsLog: React.FC<AnsweredQuestionsLogProps> = ({
  questions,
  answeredQuestions,
  correctlyAnsweredQuestions,
  wronglyAnsweredQuestions,
  revealedAnswers,
  skippedQuestions,
  onWordClick,
  pendingWord
}) => {
  const answeredList = questions.filter((q) => answeredQuestions.has(q.id));

  if (answeredList.length === 0) return null;

  return (
    <div className="max-w-3xl mx-auto mb-8 animate-fade-in px-2 sm:px-0">
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm sm:text-base">
            <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-500" />
            Answered Questions Log
          </h3>
          <span className="text-xs font-semibold bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full whitespace-nowrap">
            {answeredList.length} answered
          </span>
        </div>
        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {answeredList.map((q, index) => {
            const isCorrect = correctlyAnsweredQuestions.has(q.id);
            const isWrong = wronglyAnsweredQuestions.has(q.id);
            const isRevealed = revealedAnswers.has(q.id);
            const isSkipped = skippedQuestions.has(q.id);

            let statusIcon = null;
            let statusText = '';
            let statusColor = '';

            if (isCorrect) {
              statusIcon = <CheckCircle2 className="w-4 h-4 text-emerald-500 mr-1" />;
              statusText = 'You answered correctly';
              statusColor = 'text-emerald-700 bg-emerald-50 border-emerald-200';
            } else if (isWrong) {
              statusIcon = <XCircle className="w-4 h-4 text-red-500 mr-1" />;
              statusText = 'You answered incorrectly';
              statusColor = 'text-red-700 bg-red-50 border-red-200';
            } else if (isRevealed) {
              statusIcon = <Eye className="w-4 h-4 text-orange-500 mr-1" />;
              statusText = 'Answer revealed';
              statusColor = 'text-orange-700 bg-orange-50 border-orange-200';
            } else if (isSkipped) {
              statusIcon = <SkipForward className="w-4 h-4 text-gray-500 mr-1" />;
              statusText = 'Question skipped';
              statusColor = 'text-gray-700 bg-gray-50 border-gray-200';
            }

            return (
              <div key={q.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs sm:text-sm font-semibold text-gray-500">Question {index + 1}</p>
                  {statusText && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColor}`}>
                      {statusIcon}
                      {statusText}
                    </span>
                  )}
                </div>
                <p className="text-sm sm:text-base text-gray-900 font-medium mb-3 leading-relaxed">
                  <ClickableText text={q.question} onWordClick={onWordClick} pendingWord={pendingWord} />
                </p>
                <div className="flex items-start gap-2 sm:gap-3 bg-emerald-50 text-emerald-800 p-3 sm:p-4 rounded-lg border border-emerald-100">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-emerald-600 block mb-0.5">Correct Answer</span>
                    <span className="text-sm sm:text-base font-medium break-words">
                      <ClickableText text={q.options[q.correctIndex]} onWordClick={onWordClick} pendingWord={pendingWord} />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
